import {alpha, ThemeOptions} from '@mui/material/styles';
import {defaultTheme} from 'react-admin';

/**
 * MUI theme used within the Admin dashboard
 */
export const adminTheme: ThemeOptions = {
    ...defaultTheme,
    typography: {
        ...defaultTheme.typography,
        fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
    },
    palette: {
        ...defaultTheme.palette,
        text: {
            primary: '#110E0E',
            secondary: '#110E0E99',
        },
        primary: {
            ...defaultTheme.palette?.primary,
            main: '#FD6B54',
            contrastText: '#ffffff',
        },
        secondary: {
            ...defaultTheme.palette?.secondary,
            main: '#FFFCDD',
            contrastText: '#FD6B54',
        },
        success: {
            ...defaultTheme.palette?.success,
            main: '#5CC99F',
            dark: '#24A376',
        },
        error: {
            ...defaultTheme.palette?.error,
            main: '#E11F09',
        },
        background: {
            ...defaultTheme.palette?.background,
            default: '#f5f5f7',
            paper: '#ffffff',
        },
    },
    shape: {
        ...defaultTheme.shape,
        borderRadius: 10,
    },
    components: {
        ...defaultTheme.components,
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: ({theme}) => ({
                    textTransform: 'none',
                    fontWeight: 600,
                    letterSpacing: '0.01em',
                    borderRadius: theme.shape.borderRadius,
                }),
                containedPrimary: ({theme}) => ({
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
                    },
                }),
                sizeLarge: ({theme}) => ({
                    minHeight: 48,
                    paddingLeft: theme.spacing(2.5),
                    paddingRight: theme.spacing(2.5),
                }),
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                },
            },
        },
        MuiTextField: {
            ...defaultTheme.components?.MuiTextField,
            defaultProps: {
                ...defaultTheme.components?.MuiTextField?.defaultProps,
                variant: 'outlined',
                size: 'small',
                margin: 'dense',
                fullWidth: true,
            },
        },
        MuiFormControl: {
            ...defaultTheme.components?.MuiFormControl,
            defaultProps: {
                ...defaultTheme.components?.MuiFormControl?.defaultProps,
                variant: 'outlined',
                margin: 'dense',
                size: 'small',
                fullWidth: true,
            },
        },
        MuiPaper: {
            ...defaultTheme.components?.MuiPaper,
            styleOverrides: {
                ...defaultTheme.components?.MuiPaper?.styleOverrides,
                elevation1: ({theme}) => ({
                    boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.05)}, 0 6px 20px ${alpha(
                        theme.palette.common.black,
                        0.04
                    )}`,
                    border: `1px solid ${alpha(theme.palette.common.black, 0.07)}`,
                    backgroundClip: 'padding-box',
                }),
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: ({theme}) => ({
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    letterSpacing: '0.02em',
                    textTransform: 'none',
                    color: theme.palette.text.secondary,
                    backgroundColor: alpha(theme.palette.common.black, 0.03),
                    borderBottom: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
                }),
                body: ({theme}) => ({
                    fontSize: '0.875rem',
                    borderBottom: `1px solid ${alpha(theme.palette.common.black, 0.06)}`,
                }),
                paddingCheckbox: ({theme}) => ({
                    paddingLeft: theme.spacing(2),
                }),
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:last-of-type td': {
                        borderBottom: 0,
                    },
                },
            },
        },
        RaDatagrid: {
            styleOverrides: {
                root: ({theme}) => ({
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${alpha(theme.palette.common.black, 0.07)}`,
                    backgroundColor: theme.palette.background.paper,
                    overflow: 'hidden',
                    boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
                    '& .RaDatagrid-tableWrapper': {
                        borderRadius: theme.shape.borderRadius,
                    },
                    '& .RaDatagrid-headerCell': {
                        backgroundColor: alpha(theme.palette.common.black, 0.03),
                    },
                    '& .RaDatagrid-row': {
                        transition: theme.transitions.create('background-color', {
                            duration: theme.transitions.duration.shortest,
                        }),
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.045),
                        },
                    },
                    '& .RaDatagrid-rowEven': {
                        backgroundColor: alpha(theme.palette.common.black, 0.015),
                    },
                    '& .RaDatagrid-rowEven:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.055),
                    },
                }),
            },
        },
        RaList: {
            styleOverrides: {
                root: ({theme}) => ({
                    '& .RaList-main': {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing(1.25),
                    },
                }),
            },
        },
        RaListToolbar: {
            styleOverrides: {
                root: ({theme}) => ({
                    display: 'flex',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: theme.spacing(1.5),
                    width: '100%',
                    padding: theme.spacing(1, 1.5),
                    marginTop: theme.spacing(2),
                    marginBottom: theme.spacing(0.5),
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
                    boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
                    '& > span:empty': {
                        flex: 1,
                        minWidth: theme.spacing(2),
                    },
                    [theme.breakpoints.down('sm')]: {
                        flexWrap: 'wrap',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                    },
                }),
            },
        },
        RaFilterForm: {
            styleOverrides: {
                root: ({theme}) => ({
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: theme.spacing(1.5),
                    flex: '0 1 auto',
                    minWidth: 0,
                    minHeight: 0,
                    padding: 0,
                    '& .RaFilterForm-filterFormInput': {
                        marginTop: 0,
                        '& .MuiFormControl-root': {
                            marginTop: 0,
                            minWidth: 168,
                        },
                    },
                    '& .MuiOutlinedInput-root': {
                        borderRadius: theme.spacing(1),
                        backgroundColor: alpha(theme.palette.common.white, 0.96),
                        minHeight: 40,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.common.black, 0.12),
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.45),
                    },
                    [theme.breakpoints.down('sm')]: {
                        width: '100%',
                        flex: '1 1 auto',
                        '& .RaFilterForm-filterFormInput .MuiFormControl-root': {
                            minWidth: '100%',
                        },
                    },
                }),
            },
        },
        RaTopToolbar: {
            styleOverrides: {
                root: ({theme}) => ({
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: theme.spacing(1.25),
                    padding: theme.spacing(0.25, 0),
                    flexWrap: 'wrap',
                    flexShrink: 0,
                    '& .RaButton-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        letterSpacing: '0.01em',
                        borderRadius: theme.shape.borderRadius,
                        paddingLeft: theme.spacing(2.25),
                        paddingRight: theme.spacing(2.25),
                    },
                    [theme.breakpoints.down('sm')]: {
                        width: '100%',
                        justifyContent: 'stretch',
                        '& .RaButton-root': {
                            width: '100%',
                            justifyContent: 'center',
                        },
                    },
                }),
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: ({theme}) => ({
                    marginTop: theme.spacing(2.5),
                    paddingTop: theme.spacing(0.5),
                    borderTop: `1px solid ${alpha(theme.palette.common.black, 0.06)}`,
                }),
                toolbar: {
                    minHeight: 48,
                    paddingLeft: 0,
                    paddingRight: 0,
                },
            },
        },
        RaSidebarToggleButton: {
            styleOverrides: {
                root: {
                    '& .RaSidebarToggleButton-menuButtonIconClosed': {
                        transform: 'none',
                        transition: 'none',
                    },
                    '& .RaSidebarToggleButton-menuButtonIconOpen': {
                        transform: 'none',
                        transition: 'none',
                    },
                },
            },
        },
        RaMenuItemLink: {
            styleOverrides: {
                root: ({theme}) => ({
                    fontSize: '0.9375rem',
                    letterSpacing: '0.01em',
                    '& .RaMenuItemLink-icon': {
                        minWidth: theme.spacing(5),
                        justifyContent: 'center',
                        '& .MuiSvgIcon-root': {
                            fontSize: 22,
                            color: theme.palette.text.secondary,
                            opacity: 0.92,
                        },
                    },
                    '&.RaMenuItemLink-active .RaMenuItemLink-icon .MuiSvgIcon-root': {
                        color: theme.palette.primary.main,
                        opacity: 1,
                    },
                }),
            },
        },
    },
};

/**
 * MUI theme used on the public facing pages
 */
export const publicTheme: ThemeOptions = {
    ...adminTheme,
    palette: {
        ...adminTheme.palette,
        primary: {
            ...adminTheme.palette?.primary,
            main: '#110E0E',
        },
    },
    typography: {
        ...adminTheme.typography,
        fontFamily: 'Poppins, sans-serif',
    },
};
